import React from 'react';
import { twMerge } from 'tailwind-merge';
import { LoadingBars } from '@components/LoadingBars/LoadingBars';
import { ProfileImageEditButton } from '@components/Profile/Edit/ProfileImageEditButton';
import { Hero } from '@components/basics/Hero';
import { useAuth } from '@context/auth/base';
import type { FragmentType } from '@graphql/reactQuery';
import { CollectorHeroFragmentDoc } from '@graphql/reactQuery';
import { getFragment, gql } from '@graphql/reactQuery';
import { useUpdateCoverImage } from '@hooks/useUpdateCoverImage';
import { sleep } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CollectorHero on User {
    id
    bannerImage {
      id
      url
    }
  }
`);

export const CollectorHero = ({
  collectorData,
}: {
  collectorData: FragmentType<CollectorHeroFragmentDoc>;
}) => {
  const { authUser } = useAuth();
  const { id, bannerImage } = getFragment(CollectorHeroFragmentDoc, collectorData);
  const [updatePending, setUpdatePending] = React.useState(false);

  const { selectImage } = useUpdateCoverImage({
    onConfirm: () => {
      setUpdatePending(true);
    },
    onDone: async () => {
      // perceptible loading state
      await sleep(400);
      setUpdatePending(false);
    },
  });

  const hasBannerImage = !!bannerImage?.url;

  const isCurrentUser = authUser?.id === id;

  return (
    <Hero
      backgroundImageSrc={hasBannerImage ? bannerImage.url : '/images/sound_letter_s.svg'}
      variant="artist"
    >
      <div className="group absolute z-above1 h-[232px] w-full bg-gradient-to-b from-base800 via-[transparent] md:h-[300px]">
        <div className="hidden md:flex">
          <div
            className={twMerge(
              'pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden rounded-md bg-black/70 transition-opacity duration-200',
              updatePending ? 'opacity-100' : 'opacity-0',
            )}
          >
            {updatePending && (
              <LoadingBars height={undefined} barClassName="bg-white" className="m-0 h-12" />
            )}
          </div>
          {isCurrentUser && (
            <ProfileImageEditButton
              onClick={selectImage}
              className="absolute left-[50%] top-[112px] mx-auto my-auto flex items-center md:top-[150px]"
            />
          )}
        </div>
      </div>
    </Hero>
  );
};
